import { useEffect, useRef } from 'react';
import type { Line } from '@nelnela/contracts';
import type { Marks } from '../../lib/marks';
import type { Token } from '../../lib/text';
import { createDwellLedger } from '../../lib/reader';
import { Transcript, type EnMode } from '../watch/Transcript';

/** at least half a sentence on screen counts as being on the page */
const VISIBLE_RATIO = 0.5;
/** under this, a sentence went past rather than was read, and claims no exposure */
const MIN_DWELL_MS = 900;

export interface StoryReaderProps {
  /** the story's sentences as `Line`s — `lib/reader.ts` `storyLines` */
  lines: Line[];
  enMode: EnMode;
  revealed: ReadonlySet<string>;
  marks?: ReadonlyMap<string, Marks>;
  /** Me → Georgian text size (16–18); absent = the reader's own 17 */
  fontSizePx?: number;
  /**
   * M6b: the sentence being spoken aloud, or -1. A piece can be played back sentence by sentence
   * (`PiecePlayer`), and the reading-surface spec §4 already says what a playing line looks like —
   * fill only, no border, no accent — so this is `Transcript`'s own `activeIdx` and nothing new.
   */
  activeIdx?: number;
  /**
   * M6b: `'speaker'` for a dialogue piece, which has voices but no timeline. Default `'none'`,
   * so a story is unchanged.
   */
  gutter?: 'none' | 'speaker';
  onWordTap?: (token: Token, line: Line, tokIdx: number) => void;
  onChunkTap?: (itemId: string, line: Line, charStart: number, charEnd: number) => void;
  onReveal: (line: Line) => void;
  /** 0…1 down the scroll box — `5a`'s 2 px ember rule, which the screen draws */
  onProgress?: (fraction: number) => void;
  /** a sentence that was on screen long enough to have been read, with how long */
  onDwell?: (idx: number, dwellMs: number) => void;
  /** the last sentence came into view: the end of the story is reachable from here */
  onReachedEnd?: () => void;
}

/**
 * The reading surface of the story reader (`5a`).
 *
 * It is `Transcript` with the timeline taken out — `gutter="none"`, `activeIdx={-1}`,
 * `selection={null}` — and nothing else. Forking the transcript to get a serif face would have
 * meant a second implementation of tokenization, marks, chunk taps, phrase highlighting and the
 * ••• pill, which is to say a second place for every reading bug to live. What this component
 * adds is the two things a story has that a transcript does not: a scroll position that means
 * "how far through" (a transcript's position means nothing — the playhead is the progress), and
 * dwell, which is the only evidence a reader produces that a video produces for free.
 */
export function StoryReader({
  lines,
  enMode,
  revealed,
  marks,
  fontSizePx,
  activeIdx = -1,
  gutter = 'none',
  onWordTap,
  onChunkTap,
  onReveal,
  onProgress,
  onDwell,
  onReachedEnd,
}: StoryReaderProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  // Held in refs so the observer below is built once per story rather than re-attached on every
  // parent render — re-attaching would fire `enter` again for every visible row and restart the
  // clocks the ledger is keeping.
  const dwellRef = useRef(onDwell);
  dwellRef.current = onDwell;
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;
  const endRef = useRef(onReachedEnd);
  endRef.current = onReachedEnd;

  const lastIdx = lines.length - 1;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // `Transcript` owns its scroll box and exposes no ref for it, and it is not this milestone's
    // file to widen. The one element with `.nn-reading` inside our own host IS that box, and
    // reading it here keeps the change on this side of the boundary.
    const box = host.querySelector<HTMLElement>('.nn-reading');
    if (!box) return;

    const onScroll = () => {
      const span = box.scrollHeight - box.clientHeight;
      progressRef.current?.(span <= 0 ? 1 : Math.min(1, Math.max(0, box.scrollTop / span)));
    };
    onScroll(); // a story shorter than the viewport is already fully read
    box.addEventListener('scroll', onScroll, { passive: true });

    const ledger = createDwellLedger(MIN_DWELL_MS);
    const report = (rows: Array<{ idx: number; dwellMs: number }>) => {
      for (const r of rows) dwellRef.current?.(r.idx, r.dwellMs);
    };
    // Which sentences are on screen right now, so reading can be picked up again where a
    // backgrounded tab left it: the observer does not re-announce a row that never stopped
    // intersecting, so nothing else would restart those clocks.
    const onScreen = new Set<number>();
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          const now = Date.now();
          for (const e of entries) {
            const idx = Number((e.target as HTMLElement).dataset.idx);
            if (!Number.isFinite(idx)) continue;
            if (e.isIntersecting) {
              onScreen.add(idx);
              ledger.enter(idx, now);
              if (idx === lastIdx) endRef.current?.();
            } else {
              onScreen.delete(idx);
              report(ledger.leave(idx, now));
            }
          }
        },
        { root: box, threshold: VISIBLE_RATIO },
      );
      for (const row of box.querySelectorAll('[data-line-id]')) observer.observe(row);
    }

    // A tab going away mid-story is the common case, not the rare one: the learner reads the last
    // sentence and switches app. Settling on the way out means that reading counts — the event
    // queue persists whatever its own `pagehide` flush could not send, and the screen's gate does
    // not re-check `document.visibilityState` for rows that were measured (see `ALREADY_ATTENDED`
    // in `Read.tsx`). It also means the clock stops: a phone in a pocket is not reading time.
    const settle = () => report(ledger.flush(Date.now()));
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') settle();
      // Back on screen: the sentences that never left resume counting. Without this they would
      // sit at whatever they had banked when the tab went away — a sentence that had not yet
      // reached `MIN_DWELL_MS` could then be read for a full minute and still report nothing.
      else for (const idx of onScreen) ledger.enter(idx, Date.now());
    };
    window.addEventListener('pagehide', settle);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      box.removeEventListener('scroll', onScroll);
      observer?.disconnect();
      window.removeEventListener('pagehide', settle);
      document.removeEventListener('visibilitychange', onVisibility);
      settle();
    };
    // `lines` identity is the story: a different story rebuilds the ledger, which is right.
  }, [lines, lastIdx]);

  return (
    <div
      ref={hostRef}
      className="nn-reader"
      style={
        {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // `5a` is Noto Serif Georgian 17/1.75. The face and the line-height are rules in
          // `READER_CSS`, hung off this class; the size is this custom property, which the
          // `.nn-reading` rule in the protected `theme.ts` already reads. It is set here rather
          // than passed as `Transcript`'s `fontSizePx` because that prop is currently swallowed
          // by a duplicate `style` prop on Transcript's own scroll box (verified by rendering
          // it) and reaches nothing — the variable inherits and cannot be lost that way.
          '--nn-transcript-size': `${fontSizePx ?? 17}px`,
        } as React.CSSProperties
      }
    >
      <Transcript
        style={{ flex: 1 }}
        lines={lines}
        activeIdx={activeIdx}
        enMode={enMode}
        revealed={revealed}
        selection={null}
        gutter={gutter}
        {...(marks ? { marks } : {})}
        {...(onWordTap ? { onWordTap } : {})}
        {...(onChunkTap ? { onChunkTap } : {})}
        onReveal={onReveal}
      />
    </div>
  );
}
